'use client';

import {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import { Box } from '@terra/ui-styled-system';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { unifiedMergeView, getChunks } from '@codemirror/merge';
import {
  search,
  searchKeymap,
  highlightSelectionMatches,
  openSearchPanel,
} from '@codemirror/search';
import { unifiedMergeTheme, searchPanelTheme } from './merge-theme';
import { chunkHighlightExtension, setChunkHighlight } from './chunk-highlight';
import { comparerDiffConfig } from './diff-config';

export type CodeMirrorUnifiedViewHandle = {
  goToNext: () => void;
  goToPrev: () => void;
  getChunkCount: () => number;
  openSearch: () => void;
};

type CodeMirrorUnifiedViewProps = {
  leftContent: string;
  rightContent: string;
  collapseUnchanged?: boolean;
};

export const CodeMirrorUnifiedView = forwardRef<
  CodeMirrorUnifiedViewHandle,
  CodeMirrorUnifiedViewProps
>(function CodeMirrorUnifiedView(
  { leftContent, rightContent, collapseUnchanged },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorViewRef = useRef<EditorView | null>(null);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(-1);

  useEffect(() => {
    if (!containerRef.current) return;

    const view = new EditorView({
      doc: rightContent,
      extensions: [
        unifiedMergeTheme,
        lineNumbers(),
        EditorState.readOnly.of(true),
        search({ top: true }),
        keymap.of(searchKeymap),
        highlightSelectionMatches(),
        searchPanelTheme,
        chunkHighlightExtension,
        unifiedMergeView({
          original: leftContent,
          highlightChanges: true,
          gutter: true,
          mergeControls: false,
          syntaxHighlightDeletions: true,
          diffConfig: comparerDiffConfig,
          collapseUnchanged: collapseUnchanged
            ? { margin: 3, minSize: 4 }
            : undefined,
        }),
      ],
      parent: containerRef.current,
    });

    editorViewRef.current = view;
    setCurrentChunkIndex(-1);

    return () => {
      view.destroy();
      editorViewRef.current = null;
    };
  }, [leftContent, rightContent, collapseUnchanged]);

  const getChunksFromState = useCallback(() => {
    const view = editorViewRef.current;

    if (!view) return [];

    return getChunks(view.state)?.chunks ?? [];
  }, []);

  const scrollToChunk = useCallback(
    (index: number) => {
      const view = editorViewRef.current;
      const chunks = getChunksFromState();

      if (!view || chunks.length === 0) return;

      const clampedIndex = Math.max(0, Math.min(index, chunks.length - 1));

      setCurrentChunkIndex(clampedIndex);

      const chunk = chunks[clampedIndex];

      view.dispatch({
        effects: [
          setChunkHighlight.of({ from: chunk.fromB, to: chunk.endB }),
          EditorView.scrollIntoView(chunk.fromB, { y: 'center' }),
        ],
      });
    },
    [getChunksFromState],
  );

  const goToNext = useCallback(() => {
    const chunks = getChunksFromState();

    if (chunks.length === 0) return;
    const nextIndex =
      currentChunkIndex + 1 >= chunks.length ? 0 : currentChunkIndex + 1;

    scrollToChunk(nextIndex);
  }, [currentChunkIndex, getChunksFromState, scrollToChunk]);

  const goToPrev = useCallback(() => {
    const chunks = getChunksFromState();

    if (chunks.length === 0) return;
    const prevIndex =
      currentChunkIndex - 1 < 0 ? chunks.length - 1 : currentChunkIndex - 1;

    scrollToChunk(prevIndex);
  }, [currentChunkIndex, getChunksFromState, scrollToChunk]);

  const getChunkCount = useCallback(() => {
    return getChunksFromState().length;
  }, [getChunksFromState]);

  const openSearch = useCallback(() => {
    const view = editorViewRef.current;

    if (!view) return;
    view.focus();
    openSearchPanel(view);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      goToNext,
      goToPrev,
      getChunkCount,
      openSearch,
    }),
    [goToNext, goToPrev, getChunkCount, openSearch],
  );

  return (
    <Box
      ref={containerRef}
      h="full"
      w="full"
      overflow="hidden"
      className="cm-unified-wrapper"
    />
  );
});
